//@flow
import React, {useState} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {PlayArrow} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import YoutubePlayer from 'react-youtube';
import CustomButton from '../../../elements/CustomButton/CustomButton';

type Tutorial = {
  name: string,
  thumbnail: string,
  videoId: string,
};

const useStyles = makeStyles(theme => ({
  tutorialGrid: {
    display: 'grid',
    gap: '1rem',
    width: '100%',
    gridTemplateRows: 'repeat(auto-fit, minmax(250px, 1fr))',
    gridTemplateColumns: '100%',
    overflow: 'hidden',

    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, 32%)',
      gridTemplateRows: '100%',
    },
  },
  tutorialCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
  },
  cardThumbnail: {
    width: '100%',
    height: '100%',
    aspectRatio: '16/9',
    borderRadius: '.5rem',
    overflow: 'hidden',
    position: 'relative',
    background: colors.grey700,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      position: 'absolute',
      top: 0,
      left: 0,
    },
  },
  tutorialName: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  playButton: {
    position: 'absolute',
    width: '3rem',
    height: '3rem',
    background: 'white',
    color: colors.primary,
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    padding: 0,
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 2rem #00000040',
  },
  videoContainer: {
    width: '100%',
    borderRadius: '.75rem',
    background: colors.grey200,
  },
  videoIframe: {
    width: '100%',
    height: '100%',
    borderRadius: '.75rem',
  },
  videoWrap: {
    width: '90vw',
    height: '100%',
    aspectRatio: '16/9',
    background: colors.grey200,
    borderRadius: '.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '1200px',
    flexDirection: 'column',
  },
  modalPaper: {
    padding: '1rem',
    boxShadow: 'unset',
    borderRadius: '12px',
    alignItems: 'center',
    maxWidth: '100%',
    margin: '0 auto',
  },
  extraRecommendation: {
    background: `${colors.primary}22`,
    color: colors.primary,
    padding: '.5rem',
    width: '100%',
    borderRadius: '.5rem',
    fontSize: '.875rem',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gridColumn: '1 / -1',
    gap: '.5rem',
  },
  closeModalButton: {
    width: '100%',
    marginTop: '1rem',
    maxWidth: '200px',
  },
}));

type TutorialsProps = {
  tutorialList: Tutorial[],
};

export default function Tutorials(props: TutorialsProps) {
  const {tutorialList} = props;
  const classes = useStyles();

  const [videoId, setVideoId] = useState<string | typeof undefined>();

  return (
    <>
      <div className={classes.tutorialGrid}>
        <CustomModal
          open={!!videoId}
          onClose={() => {
            setVideoId(undefined);
          }}
          classes={{
            paper: classes.modalPaper,
          }}
        >
          <div className={classes.videoWrap}>
            <YoutubePlayer
              videoId={videoId}
              style={{
                aspectRatio: '16/9',
              }}
              className={classes.videoContainer}
              iframeClassName={classes.videoIframe}
              opts={{
                playerVars: {
                  autoplay: 1,
                  loop: 1,
                  widget_referrer: 'https://www.google.com/',
                },
              }}
            />
          </div>
          <CustomButton
            onClick={() => {
              setVideoId(undefined);
            }}
            className={classes.closeModalButton}
            variant="contained"
          >
            Cerrar
          </CustomButton>
        </CustomModal>
        {tutorialList.map(tutorial => (
          <TutorialCard
            key={tutorial.videoId}
            onPlay={() => {
              setVideoId(tutorial.videoId);
            }}
            name={tutorial.name}
            thumbnail={tutorial.thumbnail}
          />
        ))}
      </div>

      <button
        className={classes.extraRecommendation}
        onClick={() => {
          setVideoId('1ONfJ-fKmjw');
        }}
      >
        <strong>Recomendación extra:</strong> Cómo convertir una imagen a URL
      </button>
    </>
  );
}

type Props = {
  name: string,
  thumbnail: string,
  onPlay: () => void,
};

function TutorialCard(props: Props) {
  const {name, onPlay, thumbnail} = props;

  const classes = useStyles();
  return (
    <button className={classes.tutorialCard} onClick={onPlay}>
      <picture className={classes.cardThumbnail}>
        <img src={thumbnail} alt={name} />

        <div className={classes.playButton}>
          <PlayArrow />
        </div>
      </picture>

      <Typography className={classes.tutorialName}>{name}</Typography>
    </button>
  );
}
